<template>
  <div v-if="clockInList.length">
    <List
      v-model="loading"
      :finished="finished"
      finished-text="没有更多了"
      @load="handlerLoadClock"
    >
      <div class="list-card" v-for="item in clockInList" :key="item.id">
        <ClockInCard
          :key="item.id"
          :dataObj="item"
          :isAdmin="isAdmin"
          size="26.93vw"
          @star="handlerStar"
          @clickComment="handlerComment"
          @loadMoreComment="handlerLoadMoreComment"
          @my-delete="handlerDelete"
        />
      </div>

      <template v-slot:loading>
        <div class="load-clock-list">
          <img src="@/assets/clock/play_icon_loading.png" alt="loading" />
          加载中...
        </div>
      </template>
    </List>

    <ClockInComment
      :show.sync="showInput"
      :cardId="commentData.user_punch_id"
      :replyId="commentData.reply_id"
      :replyName="commentData.name"
      :detailId="detailId"
      @success="handlerCommentSuccess"
    />
  </div>
</template>

<script>
import { List, Toast } from 'vant';
import { getOtherClockIn, getMoreComments, setDeleteClockIn } from '@/api/clockin';
import ClockInCard from '@/components/clockInCard/index.vue';
import ClockInComment from '@/components/ClockInComment/index.vue';
import {
  onMounted, ref, watch, computed
} from '@vue/composition-api';
import store from '@/store/index';
import router from '@/router';

export default {
  props: {
    detailId: {
      type: Number,
      required: true,
    },
    showNoComment: {
      type: Number,
      default: 0
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    classId: {
      type: Number,
      default: 0
    },
    groupId: {
      type: Number,
      default: -1
    }
  },
  components: {
    List,
    ClockInCard,
    ClockInComment
  },
  setup(props) {
    /**
     * @description 加载其他人的打卡数据
     * @param {number} id 学习小节ID
     * @param {number} offset 偏移量，默认0
     * @param {number} showNoComment 0 全部， 1 未点评的数据
     * @returns {promise} {total, list}
     */
    const loadOtherClockIn = (id, offset = 0, showNoComment) => new Promise((resolve, reject) => {
      const data = {
        detail_id: id,
        limit: 10,
        offset,
        comment_limit: 5
      };

      if (showNoComment === 1) {
        data.is_assistant_reply = 0;
      }

      if (props.classId) {
        data.class_id = props.classId;
      }

      if (props.groupId !== -1) {
        data.group_id = props.groupId;
      }

      getOtherClockIn(data)
        .then((res) => {
          const val = res.data;
          const backVal = {
            total: val.count,
          };

          if (!val.items.length) {
            backVal.list = [];
            resolve(backVal);
            return;
          }

          backVal.list = val.items.map(item => ({
            id: item.id,
            myData: item.user,
            level: item.level,
            date: item.create_time,
            isTop: item.top_time,
            content: item.content,
            video: item.video_url,
            record: item.multi_voices,
            img: item.multi_images,
            star: item.like_user_metas,
            commentCount: item.comment_count,
            commentList: item.comment_items,
            groupName: item.group.name
          }));

          resolve(backVal);
        })
        .catch(() => {
          reject();
        });
    });

    const clockInList = ref([]);
    const total = ref(0);
    const loading = ref(false);
    const finished = ref(false);

    const init = async () => {
      finished.value = false;
      total.value = 0;
      clockInList.value = [];

      const data = await loadOtherClockIn(props.detailId, 0, props.showNoComment);

      total.value = data.total;
      clockInList.value = data.list;

      loading.value = false;
      if (data.list.length >= data.total) {
        finished.value = true;
      }
    };

    /**
     * @description 移除打卡列表里的一条数据
     * @param {string} id 打卡ID
     */
    const removeClockIn = (id) => {
      if (clockInList.value.length <= 1) {
        clockInList.value = [];
        return;
      }

      clockInList.value = clockInList.value.filter(item => item.id !== id);
    };

    onMounted(async () => {
      await init();
    });

    watch([() => props.detailId, () => props.showNoComment, () => props.groupId], async () => {
      await init();
    });

    const handlerLoadClock = () => {
      const offset = clockInList.value.length;

      loadOtherClockIn(props.detailId, offset, props.showNoComment).then((data) => {
        const len = data.list.length;

        if (!len) {
          loading.value = false;
          finished.value = true;
          return;
        }

        clockInList.value.push(...data.list);
        total.value = data.total;

        loading.value = false;

        if (offset + len >= data.total) {
          finished.value = true;
        }
      });
    };

    const handlerStar = (data) => {
      const idx = clockInList.value.findIndex(item => item.id === data.id);

      if (data.isLike) {
        clockInList.value[idx].star.push(store.state.user);
        return;
      }

      const index = clockInList.value[idx].star.findIndex(
        item => item.id === store.state.user.id
      );

      clockInList.value[idx].star.splice(index, 1);
    };

    const showInput = ref(false);
    const commentData = ref({
      user_punch_id: '', // 用户打卡ID
      reply_id: '', // 回复的评论ID
      name: '', // 回复的人的名字
    });
    const handlerComment = (data) => {
      // 班委点评
      if (!data.reply_id && props.isAdmin) {
        const value = clockInList.value.find(item => item.id === data.user_punch_id);

        if (value) {
          const dataObj = {
            id: value.id,
            content: value.content,
            imgs: value.img,
            video: value.video,
            records: value.record,
            user: {
              name: value.myData.nickname,
              avatar: value.myData.portrait
            },
            isTop: !!value.isTop
          };

          sessionStorage.adminComment = JSON.stringify(dataObj);
        }

        // 跳转到点评页面
        router.push({
          path: '/admin-comment',
          query: {
            id: props.detailId,
            classId: props.classId
          }
        });
        return;
      }

      commentData.value = {
        ...commentData.value,
        ...data,
      };
      showInput.value = true;
    };
    const handlerCommentSuccess = (data) => {
      showInput.value = false;
      const id = commentData.value.user_punch_id;
      const idx = clockInList.value.findIndex(item => item.id === id);

      clockInList.value[idx].commentList.push(data);
      clockInList.value[idx].commentCount += 1;
    };
    const handlerLoadMoreComment = (data) => {
      const {
        // eslint-disable-next-line camelcase
        up_id,
        limit,
        // eslint-disable-next-line camelcase
        last_id,
      } = data;

      // eslint-disable-next-line camelcase
      const idx = clockInList.value.findIndex(item => item.id === up_id);

      getMoreComments({ up_id, limit, last_id }).then((res) => {
        const value = res.data;

        const comments = value.items.filter(item => !clockInList.value[idx].commentList.some(val => val.id === item.id));

        clockInList.value[idx].commentList.push(...comments);
        clockInList.value[idx].commentCount = value.count;
      });
    };

    const handlerDelete = (id) => {
      if (!id) {
        return;
      }

      setDeleteClockIn(id).then((res) => {
        if (res.success) {
          removeClockIn(id);
          Toast('删除成功');
        } else {
          Toast('删除失败');
        }
      });
    };

    // 响应打卡详情页、班委点评页对数据的修改
    const updateData = computed(() => store.state.isUpdateCard);
    watch(updateData, (val) => {
      if (!clockInList.value.length) {
        return;
      }

      const idx = clockInList.value.findIndex(item => item.id === val.id);
      if (idx === -1) {
        return;
      }

      if (val.isWho === 1) {
        // 更新点赞列表

        store.commit('SET_ISUPDATE_CARD', {
          id: '',
          isWho: 0
        });

        if (val.stars) {
          clockInList.value[idx].star.push(store.state.user);
          return;
        }

        const index = clockInList.value[idx].star.findIndex(
          item => item.id === store.state.user.id
        );

        if (index === -1) {
          return;
        }

        clockInList.value[idx].star.splice(index, 1);
      } else if (val.isWho === 2) {
        // 更新评论列表

        store.commit('SET_ISUPDATE_CARD', {
          id: '',
          isWho: 0
        });

        if (clockInList.value[idx].commentList.length >= 5) {
          clockInList.value[idx].commentCount += 1;
          return;
        }

        const data = {
          up_id: clockInList.value[idx].id,
          limit: 5
        };

        if (clockInList.value[idx].commentList.length) {
          data.last_id = clockInList.value[idx].commentList[clockInList.value[idx].commentList.length - 1].id;
        }

        getMoreComments(data).then((res) => {
          const value = res.data;
          const comments = value.items.filter(item => !clockInList.value[idx].commentList.some(ele => ele.id === item.id));

          clockInList.value[idx].commentList.push(...comments);
          clockInList.value[idx].commentCount = value.count;
        });
      } else if (val.isWho === 3) {
        // 班委点评后，更新数据
        if (val.adminComment.level) {
          clockInList.value[idx].level = val.adminComment.level;
        }

        if (val.adminComment.content && clockInList.value[idx].commentList.length >= 5) {
          clockInList.value[idx].commentCount += 1;
        }

        if (val.adminComment.content && clockInList.value[idx].commentList.length < 5) {
          clockInList.value[idx].commentList.push(val.adminComment.content);
          clockInList.value[idx].commentCount += 1;
        }

        if (val.adminComment.isTop !== undefined) {
          clockInList.value[idx].isTop = val.adminComment.isTop;
        }

        store.commit('SET_ISUPDATE_CARD', {
          id: '',
          isWho: 0
        });
      }
    }, { deep: true });


    return {
      clockInList,
      loading,
      finished,
      showInput,
      commentData,
      handlerLoadClock,
      handlerStar,
      handlerComment,
      handlerCommentSuccess,
      handlerLoadMoreComment,
      handlerDelete
    };
  },
};
</script>

<style lang="stylus" scoped>
.list-card {
  margin: 0 12px;
  box-sizing: border-box;
}

.load-clock-list {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #999;

  img {
    margin: 0 5px;
    width: 24px;
    height: 24px;
  }
}
</style>
